import { useState } from 'react';
import { X, AlertTriangle, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { deleteUser } from '../lib/admin';

interface DeleteUserConfirmProps {
  userId: string;
  userEmail: string;
  userName: string;
  onClose: () => void;
  onUserDeleted: () => void;
}

export function DeleteUserConfirm({
  userId,
  userEmail,
  userName,
  onClose,
  onUserDeleted
}: DeleteUserConfirmProps) {
  const [isDeleting, setIsDeleting] = useState(false);
  const [deletionReason, setDeletionReason] = useState('');
  const [confirmEmail, setConfirmEmail] = useState('');

  const handleDelete = async () => {
    if (confirmEmail !== userEmail) {
      toast.error('Email confirmation does not match');
      return;
    }

    try {
      setIsDeleting(true);
      await deleteUser(userId, deletionReason || 'Admin deletion');
      toast.success('User deleted successfully');
      onUserDeleted();
      onClose();
    } catch (error) {
      console.error('Error deleting user:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to delete user';
      toast.error(errorMessage);
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 backdrop-blur-sm flex items-center justify-center z-50">
      <div className="bg-scale-navy rounded-lg shadow-xl max-w-lg w-full mx-4 border border-red-500/20">
        <div className="p-6 border-b border-gray-800">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-red-500/10 rounded-full flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-red-400" />
              </div>
              <h2 className="text-2xl font-bold text-white">Delete User</h2>
            </div>
            <button
              onClick={onClose}
              disabled={isDeleting}
              className="text-gray-400 hover:text-white transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        <div className="p-6 space-y-6">
          <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4">
            <p className="text-red-400 font-semibold mb-2">Warning: This action cannot be undone!</p>
            <p className="text-gray-300 text-sm">
              Deleting this user will permanently remove:
            </p>
            <ul className="list-disc list-inside text-gray-300 text-sm mt-2 space-y-1">
              <li>User account and authentication credentials</li>
              <li>All user data and selections</li>
              <li>Local subscription references</li>
              <li>Integration settings</li>
              <li>Payment history references</li>
            </ul>
            <p className="text-gray-300 text-sm mt-3">
              Accounts with a live subscription cannot be deleted. Cancel billing in Stripe and wait for the verified webhook to update ScaleSmall first. Stripe accounting records remain in Stripe.
            </p>
          </div>

          <div className="bg-scale-darker p-4 rounded-lg space-y-2">
            <div>
              <span className="text-gray-400 text-sm">User:</span>
              <span className="text-white ml-2">{userName}</span>
            </div>
            <div>
              <span className="text-gray-400 text-sm">Email:</span>
              <span className="text-white ml-2">{userEmail}</span>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-400 mb-2">
              Deletion Reason (Optional)
            </label>
            <textarea
              value={deletionReason}
              onChange={(e) => setDeletionReason(e.target.value)}
              placeholder="Enter reason for deletion (for audit log)..."
              rows={3}
              disabled={isDeleting}
              className="w-full px-3 py-2 bg-scale-darker border border-gray-700 rounded-md text-white placeholder-gray-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-red-400 mb-2">
              Type user's email to confirm deletion
            </label>
            <input
              type="email"
              value={confirmEmail}
              onChange={(e) => setConfirmEmail(e.target.value)}
              placeholder={userEmail}
              disabled={isDeleting}
              className="w-full px-3 py-2 bg-scale-darker border border-red-500/30 rounded-md text-white placeholder-gray-500 focus:border-red-500/50 focus:outline-none"
            />
          </div>
        </div>

        <div className="p-6 border-t border-gray-800 flex justify-end space-x-3">
          <button
            onClick={onClose}
            disabled={isDeleting}
            className="px-4 py-2 bg-gray-700 text-white rounded-md hover:bg-gray-600 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleDelete}
            disabled={isDeleting || confirmEmail !== userEmail}
            className="flex items-center px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isDeleting ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Deleting...
              </>
            ) : (
              'Delete User'
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
